// packages/shared-components/src/components/ColorPicker/ColorPicker.tsx

import { useState, useRef, useEffect, useMemo } from "react";
import { ColorPickerProps } from "./types";
import { ColorPickerPanel } from "./ColorPickerPanel";
import { getDefaultPresetColors } from "./colorUtils";
import { hexToRgba, rgbaToHex, getAlphaFromRgba } from "../../utils/color";
import { DEFAULT_PICKER_COLORS } from "../../config/color"; // 新增导入
import styles from "./ColorPicker.module.css";

/**
 * 检测颜色格式
 */
function detectColorFormat(color: string): "hex" | "rgba" | "unknown" {
  if (/^#[0-9A-F]{6}$/i.test(color)) {
    return "hex";
  }
  if (/^rgba?\(\d+,\s*\d+,\s*\d+(?:,\s*[\d.]+)?\)$/i.test(color)) {
    return "rgba";
  }
  return "unknown";
}

/**
 * 检测颜色数组格式
 */
function detectColorsFormat(colors: string[]): {
  format: "hex" | "rgba" | null;
  isMixed: boolean;
  invalidColors: string[];
} {
  if (colors.length === 0) {
    return { format: null, isMixed: false, invalidColors: [] };
  }

  const formats = colors.map(detectColorFormat);
  const invalidColors = colors.filter((_, i) => formats[i] === "unknown");
  const validFormats = formats.filter((f) => f !== "unknown");

  if (invalidColors.length > 0) {
    return { format: null, isMixed: false, invalidColors };
  }

  const uniqueFormats = Array.from(new Set(validFormats));
  const isMixed = uniqueFormats.length > 1;
  const format = isMixed ? null : (uniqueFormats[0] as "hex" | "rgba");

  return { format, isMixed, invalidColors: [] };
}

/**
 * 颜色选择器
 *
 * 显示颜色块，点击选中，点击编辑图标修改颜色
 * 自动检测颜色格式（hex 或 rgba），不支持混用
 *
 * @example
 * ```tsx
 * // HEX 格式（自动检测）
 * <ColorPicker
 *   value="#FF5722"
 *   colors={['#FF5722', '#2196F3']}
 *   onChange={setColor}
 *   onColorEdit={(index, color) => {
 *     const newColors = [...colors]
 *     newColors[index] = color
 *     setColors(newColors)
 *   }}
 * />
 *
 * // RGBA 格式（自动检测）
 * <ColorPicker
 *   value="rgba(255, 87, 34, 0.8)"
 *   colors={['rgba(255, 87, 34, 0.8)', 'rgba(33, 150, 243, 0.6)']}
 *   onChange={setColor}
 *   onColorEdit={(index, color) => {
 *     const newColors = [...colors]
 *     newColors[index] = color
 *     setColors(newColors)
 *   }}
 * />
 * ```
 */
export function ColorPicker({
  value,
  colors = DEFAULT_PICKER_COLORS, // 使用常量
  onChange,
  onColorEdit,
  disabled = false,
  count = 8,
  itemsPerRow = 4,
  className = "",
}: ColorPickerProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingColor, setEditingColor] = useState<string>("#2196F3");
  const [editingAlpha, setEditingAlpha] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const isOpen = editingIndex !== null;

  // 检测颜色格式
  const {
    format: colorFormat,
    isMixed,
    invalidColors,
  } = useMemo(() => detectColorsFormat(colors), [colors]);

  // 如果检测到混用或无效格式，显示错误提示
  if (isMixed) {
    return (
      <div className={`${styles.colorPicker} ${styles.error} ${className}`}>
        <div className={styles.errorMessage}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>颜色格式混用错误</span>
          <p>colors 数组不支持 HEX 和 RGBA 混用，请统一使用同一种格式</p>
        </div>
      </div>
    );
  }

  if (invalidColors.length > 0) {
    return (
      <div className={`${styles.colorPicker} ${styles.error} ${className}`}>
        <div className={styles.errorMessage}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>颜色格式无效</span>
          <p>
            以下颜色格式不正确: {invalidColors.join(", ")}
            <br />
            请使用 HEX (#FF5722) 或 RGBA (rgba(255, 87, 34, 0.8)) 格式
          </p>
        </div>
      </div>
    );
  }

  // 确保颜色数组有足够的数量
  const displayColors = [...colors];
  const fillColor = colorFormat === "hex" ? "#2196F3" : "rgba(33, 150, 243, 1)";
  while (displayColors.length < count) {
    displayColors.push(fillColor);
  }
  const limitedColors = displayColors.slice(0, count);

  // 判断是否为单色模式
  const isSingleMode = count === 1;

  // 处理颜色块点击（选中）
  const handleColorClick = (color: string, key: number) => {
    if (disabled) return;
    console.log("选中颜色:", color);
    onChange?.(color, key);
  };

  // 处理编辑图标点击
  const handleEditClick = (
    e: React.MouseEvent,
    color: string,
    index: number
  ) => {
    e.stopPropagation();
    if (disabled) return;
    setEditingIndex(index);

    // 转换为 HEX 格式供面板使用，并保存原始 alpha 值
    if (colorFormat === "rgba") {
      const hexColor = rgbaToHex(color);
      const alpha = getAlphaFromRgba(color);
      setEditingColor(hexColor);
      setEditingAlpha(alpha);
    } else {
      setEditingColor(color);
      setEditingAlpha(1);
    }
  };

  // 处理颜色编辑
  const handleColorChange = (newHexColor: string) => {
    setEditingColor(newHexColor);
    if (editingIndex !== null) {
      // 根据格式转换颜色
      const outputColor =
        colorFormat === "hex"
          ? newHexColor
          : hexToRgba(newHexColor, editingAlpha);

      onColorEdit?.(editingIndex, outputColor);
    }
  };

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        popoverRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setEditingIndex(null);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // ESC 键关闭
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setEditingIndex(null);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={`${styles.colorPicker} ${disabled ? styles.disabled : ""} ${
        isSingleMode ? styles.singleMode : ""
      } ${className}`}
    >
      {/* 单色模式 */}
      {isSingleMode ? (
        <div
          className={`${styles.singleColor} ${
            value === limitedColors[0] ? styles.colorBlockActive : ""
          }`}
          style={{ backgroundColor: limitedColors[0] }}
          onClick={() => handleColorClick(limitedColors[0], 0)}
          title={limitedColors[0]}
        >
          <button
            type="button"
            className={styles.editButton}
            onClick={(e) => handleEditClick(e, limitedColors[0], 0)}
            disabled={disabled}
            aria-label="编辑颜色"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
      ) : (
        /* 多色网格模式 */
        <div
          className={styles.colorGrid}
          style={{
            gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
          }}
        >
          {limitedColors.map((color, index) => (
            <div
              key={index}
              className={`${styles.colorBlock} ${
                value === color ? styles.colorBlockActive : ""
              }`}
              style={{ backgroundColor: color }}
              onClick={() => handleColorClick(color, index)}
              title={color}
            >
              <button
                type="button"
                className={styles.editButton}
                onClick={(e) => handleEditClick(e, color, index)}
                disabled={disabled}
                aria-label="编辑颜色"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 颜色选择面板弹出层 */}
      {isOpen && (
        <div ref={popoverRef} className={styles.popover}>
          <div className={styles.popoverContent}>
            <ColorPickerPanel
              value={editingColor}
              onChange={handleColorChange}
              presetColors={getDefaultPresetColors(true)}
              saturationWidth={240}
              saturationHeight={200}
              hueWidth={20}
              hueHeight={200}
              presetColumns={10}
            />
          </div>
        </div>
      )}
    </div>
  );
}
